import { FC } from 'react'
import FormButtonGroup from '../elements/FormButtonGroup'
import FormSelect from '../elements/FormSelect'
import FormInput from '../elements/FormInput'
import { useStoreState, useStoreActions } from '@/store'
import { AgentSearchCriteria } from '@/types/agents'
import generics from '@/utils/generics'

const worksWithOptions = [
  { label: 'Buyer', value: 'buy' },
  { label: 'Seller', value: 'sell' },
  { label: 'Both', value: 'buy-sell' }
]

const listingPriceRangeOptions = [
  { label: 'Any', value: '0-99999999' },
  { label: '$0 - $249,999', value: '0-249999' },
  {
    label: '$250,000 - $499,999',
    value: '250000-499999'
  },
  {
    label: '$500,000 - $749,999',
    value: '500000-749999'
  },
  {
    label: '$750,000 - $999,999',
    value: '750000-999999'
  },
  {
    label: '$1M and above',
    value: '1000000-99999999'
  }
]

const listingPropertyTypeOptions = [
  { label: 'Any', value: 'single_family,condominium,townhome' },
  { label: 'Single-family homes', value: 'single_family' },
  { label: 'Condos', value: 'condominium' },
  { label: 'Townhomes', value: 'townhome' }
]

const Filters: FC = () => {
  const { zipcode, price_from, price_to, property_type, buy_sell } =
    useStoreState(state => state.search.dimensions)
  const validators = useStoreState(state => state.search.validators)
  const { setDimensions } = useStoreActions(actions => actions.search)
  const { onInputTracking, resetEvents } = useStoreActions(
    actions => actions.tracking
  )

  const zipCodeUpdate = (value: string) => {
    if (value.length === 5 && !isNaN(Number(value))) {

      const fieldInputtedPayload = generics.buildFieldInputtedPayload(
        'zipcode',
        'ZIP code',
        value
      )
      onInputTracking(fieldInputtedPayload)
    }

    setDimensions({ zipcode: value })
  }

  const worksWithUpdate = (value: 'buy' | 'sell' | 'buy-sell') => {
    const fieldInputtedPayload = generics.buildFieldInputtedPayload(
      'workswith',
      'Works with',
      value
    )
    onInputTracking(fieldInputtedPayload)

    setDimensions({ buy_sell: value })
  }

  const priceRangeUpdate = (value: string) => {
    const fieldInputtedPayload = generics.buildFieldInputtedPayload(
      'listingpricerange',
      'Listing price range',
      value
    )
    onInputTracking(fieldInputtedPayload)

    const [price_from, price_to] = value.split('-').map(Number)
    setDimensions({ price_from, price_to })
  }

  const propertyTypeUpdate = (
    value: AgentSearchCriteria['property_type']
  ) => {
    const fieldInputtedPayload = generics.buildFieldInputtedPayload(
      'listingpropertytype',
      'Listing property type',
      value
    )
    onInputTracking(fieldInputtedPayload)

    setDimensions({ property_type: value })
  }

  return (
    <div>
      <FormInput
        label="ZIP code"
        name="zipcode"
        value={zipcode}
        onUpdate={value => zipCodeUpdate(value)}
        validators={validators.zipcode}
        type="text"
        className="mb-6"
        pattern="[0-9]+"
        maxLength={5}
      />

      <FormButtonGroup
        label="Works with"
        name="worksWith"
        value={buy_sell}
        onUpdate={(value: AgentSearchCriteria['buy_sell']) =>
          worksWithUpdate(value)
        }
        options={worksWithOptions}
        className="mb-6"
      />

      <FormSelect
        label="Listing price range"
        name="listingPriceRange"
        value={`${price_from}-${price_to}`}
        onUpdate={value => priceRangeUpdate(value)}
        options={listingPriceRangeOptions}
        className="mb-6"
      />

      <FormSelect
        label="Listing property type"
        name="listingPropertyType"
        value={property_type}
        onUpdate={(value: AgentSearchCriteria['property_type']) =>
          propertyTypeUpdate(value)
        }
        options={listingPropertyTypeOptions}
        className="mb-6"
      />
    </div>
  )
}

export default Filters
